import {
  Buffer,
  BufferUsage,
  Container,
  Geometry,
  Mesh,
  RenderTexture,
  Shader,
  type Renderer as PixiRenderer,
  type UniformGroup,
} from 'pixi.js';
import { MAX_MAGNETS, PHYSICS, VISUALS, WORLD_HEIGHT, WORLD_WIDTH } from '../config.ts';
import { FIELD_GLSL } from '../simulation/MagneticField.ts';
import type { Ferrofluid } from '../simulation/Ferrofluid.ts';
import type { MagneticField } from '../simulation/MagneticField.ts';

/**
 * Ferrofluid, drawn as metaballs.
 *
 * Four passes:
 *   1. every particle splats a smooth kernel, additively, into a density texture;
 *   2. and 3. that density is blurred, separably, horizontally then vertically;
 *   4. a single world-space quad thresholds the result into a surface, derives a normal
 *      from the density gradient, and shades it as black glossy liquid lit from above.
 *
 * The blur is what stops the surface looking like a row of overlapping circles. Threshold
 * a raw sum of kernels and the iso-line follows individual particles wherever they thin
 * out — which reads as scalloping along every edge. Blurring first pushes the lumpiness
 * below the scale of the surface, and costs two small full-screen passes.
 *
 * The particles themselves are never visible. The density texture is sized in *world*
 * units rather than screen pixels, so the fluid looks and costs the same on every device.
 */

/** Vertex corners of a splat quad, in kernel space. */
const CORNERS = [-1, -1, 1, -1, 1, 1, -1, 1];

/** Shared vertex stage for the full-screen world-space passes. */
const quadVertex = /* glsl */ `#version 300 es
in vec2 aPosition;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
uniform vec2 uWorldSize;

out vec2 vUV;
out vec2 vWorld;

void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vUV = aPosition / uWorldSize;
    vWorld = aPosition;
}
`;

const densityVertex = /* glsl */ `#version 300 es
in vec2 aPosition;
in vec2 aCorner;

uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;

out vec2 vCorner;

void main() {
    mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
    gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
    vCorner = aCorner;
}
`;

const densityFragment = /* glsl */ `#version 300 es
precision mediump float;

in vec2 vCorner;
out vec4 fragColor;

uniform float uDensityScale;

void main() {
    float radiusSq = dot(vCorner, vCorner);
    if (radiusSq >= 1.0) discard;
    // Poly6-ish kernel: smooth, compactly supported, and cheap.
    float falloff = 1.0 - radiusSq;
    float density = falloff * falloff * falloff * uDensityScale;
    fragColor = vec4(density, density, density, density);
}
`;

/**
 * Separable Gaussian, five bilinear taps per axis (a nine-tap kernel for the price of
 * five fetches). uDirection carries the axis and the texel size together.
 */
const blurFragment = /* glsl */ `#version 300 es
precision mediump float;
// The density texture is half-float where the driver allows it, and a lowp sampler — the
// ES 3.00 default — would clamp it to a range of two and quantise the interior flat.
precision mediump sampler2D;

in vec2 vUV;
out vec4 fragColor;

uniform sampler2D uSource;
uniform vec2 uDirection;

void main() {
    vec2 offset1 = uDirection * 1.3846153846;
    vec2 offset2 = uDirection * 3.2307692308;

    float sum = texture(uSource, vUV).r * 0.2270270270;
    sum += texture(uSource, vUV + offset1).r * 0.3162162162;
    sum += texture(uSource, vUV - offset1).r * 0.3162162162;
    sum += texture(uSource, vUV + offset2).r * 0.0702702703;
    sum += texture(uSource, vUV - offset2).r * 0.0702702703;

    fragColor = vec4(sum, sum, sum, sum);
}
`;

/**
 * Surface shading, lit from above.
 *
 * The camera looks straight down, so the view vector is +z everywhere and all the shape
 * information is in the normal recovered from the density gradient. That makes the
 * silhouette do most of the work: a Fresnel term concentrates the environment reflection
 * where the surface rolls away from the camera, which is what gives a flat blob the read
 * of a rounded, heavy bead of liquid.
 *
 * The spikes are the reason the fluid is rendered from a density field rather than from
 * geometry. Real ferrofluid past a critical field undergoes the Rosensweig instability:
 * the surface buckles into a standing lattice of peaks. Three facts about the real thing
 * drive how it is built here.
 *
 * It is a *height field*, not a silhouette. Seen from directly above, the peaks are
 * visible almost entirely through shading — a hexagonal array of specular glints across
 * the pool — while the outline barely changes. Spiky outlines are what you see from the
 * side. So the lattice is computed as a height, its analytic gradient tilts the surface
 * normal, and only near the rim does it also displace the density lookup.
 *
 * Spacing is a property of the liquid, not of the magnet. It is the capillary length,
 * set by surface tension against gravity, so peaks stay the same distance apart across
 * the whole crown. The lattice is therefore built from three plane waves at sixty degrees
 * — the standard hexagonal pattern — and anchored in world space rather than to any
 * magnet's angular frame.
 *
 * The pattern stands still. This is an equilibrium, not a wave: peaks do not travel and
 * do not advect with bulk flow. Fluid streams through a stationary crown. What moves is
 * the *region* that is supercritical, which follows the field as a magnet is dragged, so
 * peaks wink in and out in place rather than sliding along.
 */
const surfaceFragment = /* glsl */ `#version 300 es
precision highp float;

in vec2 vUV;
in vec2 vWorld;
out vec4 fragColor;

uniform highp sampler2D uDensity;
uniform vec2  uTexel;
uniform float uThreshold;
uniform float uEdge;
uniform float uNormalStrength;
uniform float uBodyRelief;
uniform float uAbsorption;
uniform vec2  uLightDirection;
uniform vec2  uShadowOffset;
uniform vec4  uMagnets[${MAX_MAGNETS}];
uniform float uMagnetCount;
uniform vec3  uFieldParams;
uniform vec2  uWorldSize;
uniform vec4  uSpikeParams;
uniform vec3  uSpikeShape;

${FIELD_GLSL}

#define TAU 6.28318530718

const vec3 BODY      = vec3(0.008, 0.009, 0.014);
/** Below the horizon: the lit board the fluid is lying on, seen almost edge-on. */
const vec3 ENV_TABLE = vec3(0.150, 0.055, 0.120);
/**
 * Above it: a dim, cool room.
 *
 * Dark on purpose. Straight overhead is what the *level* interior of a pool mirrors, and
 * a pool of black liquid on a table is level across almost all of its area — the doming
 * is confined to a meniscus at the rim. So whatever sits directly above the board is what
 * most of the blob will be, and anything but near-black there turns it into a flat grey
 * disc. The bright source belongs where the tilted rim normals point, not overhead.
 */
const vec3 ENV_ROOM  = vec3(0.030, 0.042, 0.075);
/** A cool bounce from the side away from the key. */
const vec3 ENV_FILL  = vec3(0.110, 0.190, 0.340);
/**
 * A bright band right at the horizon.
 *
 * In a real room the wall behind the table is the brightest thing a grazing reflection
 * can find, and it is what puts a rim on a bead of anything dark. Without it the rim
 * mirrors the table, which is darker than the room, and the silhouette reads as an
 * outline drawn around a hole rather than as an edge rolling away from the camera.
 */
const vec3 ENV_HORIZON = vec3(0.62, 0.70, 0.95);
/** The softbox. Well above 1.0 on purpose — this is what the bloom is for. */
const vec3 ENV_KEY   = vec3(3.10, 2.95, 2.70);
const vec3 SPECULAR  = vec3(1.0, 0.98, 0.94);
const vec3 POSITIVE  = vec3(1.0, 0.32, 0.38);
const vec3 NEGATIVE  = vec3(0.30, 0.68, 1.0);

float density(vec2 uv) {
    return texture(uDensity, uv).r;
}

/** The thresholded surface: 0 outside the fluid, 1 inside. */
float coverage(float d) {
    return smoothstep(uThreshold - uEdge, uThreshold + uEdge, d);
}

/**
 * Hexagonal lattice of peaks, with its analytic gradient.
 *
 * Three plane waves sixty degrees apart sum to maxima on a triangular lattice — the same
 * arrangement the real instability picks. Returns (height, d/dx, d/dy); the gradient is
 * exact rather than finite-differenced, which matters because it is what the normal is
 * built from and four extra texture-free evaluations per pixel would not be free.
 */
vec3 hexLattice(vec2 p, float wavelength) {
    float k = TAU / wavelength;
    const vec2 A = vec2(1.0, 0.0);
    const vec2 B = vec2(0.5, 0.86602540);
    const vec2 C = vec2(-0.5, 0.86602540);

    float pa = dot(p, A) * k;
    float pb = dot(p, B) * k;
    float pc = dot(p, C) * k;

    float sum = cos(pa) + cos(pb) + cos(pc);
    vec2 gradient = -k * (A * sin(pa) + B * sin(pb) + C * sin(pc));

    // Peaks sit at sum = 3, the flat ground at sum = -1.5.
    return vec3(clamp((sum + 1.5) / 4.5, 0.0, 1.0), gradient / 4.5);
}

/**
 * How unstable the surface is here, and which way is "away from the magnet".
 *
 * The threshold is deliberately narrow: the Rosensweig bifurcation is subcritical, so
 * peaks appear abruptly rather than growing in proportion to the field. The magnet's
 * ignition state lowers its own threshold, so a crown that has already formed survives a
 * weaker field than it needed to appear — the hysteresis, modelled per magnet rather than
 * per point, which is all that is visible without keeping a feedback texture.
 *
 * Returns (mask, outward direction).
 */
vec3 spikeField(vec2 p) {
    float mask = 0.0;
    vec2 outward = vec2(0.0);

    int count = int(uMagnetCount);
    for (int i = 0; i < MAX_MAGNETS; i++) {
        if (i >= count) break;
        vec4 magnet = uMagnets[i];
        vec2 delta = p - magnet.xy;
        float distance = length(delta);
        if (distance < 1.0) continue;

        float clamped = max(distance, uFieldParams.z);
        float force = abs(magnet.z) * uFieldParams.x / pow(clamped, uFieldParams.y);
        // magnet.w is this magnet's ignition, 0..1.
        float critical = uSpikeParams.z * mix(1.0, uSpikeParams.w, magnet.w);
        float contribution = smoothstep(critical, critical * 1.16, force);
        if (contribution <= 0.001) continue;

        // Overlapping magnets should not stack into a taller crown, so take the strongest
        // rather than the sum.
        mask = max(mask, contribution);
        outward += (delta / distance) * contribution;
    }

    float length2 = dot(outward, outward);
    return vec3(mask, length2 > 1e-8 ? outward * inversesqrt(length2) : vec2(0.0));
}

/**
 * The room, sampled along a direction.
 *
 * This term decides whether the fluid reads as liquid or as a hole cut in the board,
 * because seen from directly overhead a still pool is simply a mirror pointing at the
 * ceiling: what is on the ceiling is what you see. A point light gives one small glint
 * and leaves the rest of the pool black, which is what was happening here. A broad source
 * hung overhead instead fills the flat interior with a soft sheen and then slides off it
 * as the surface rolls away towards the rim, and that slide is the whole read of a
 * rounded, heavy bead. It is also why every photograph of ferrofluid is lit with a
 * softbox and not a lamp.
 *
 * Three parts: a horizon splitting the table below from the room above, one elongated key
 * windowed in its own frame, and a wide fill opposite it so the far side of a bead is
 * coloured rather than merely dark. The rough argument widens all three together,
 * standing in for surface detail finer than the pixel grid.
 */
vec3 environmentAt(vec3 r, float rough) {
    float edge = 0.04 + rough * 0.55;
    vec3 color = mix(ENV_TABLE, ENV_ROOM, smoothstep(-edge, edge, r.z));

    // Hung high and pulled towards the light direction, so the whole board agrees on
    // where the lamp is. Windowed along two axes rather than by angle from its centre: a
    // radially symmetric highlight reads as CG, an elongated one reads as a panel.
    // Elevation matters more than anything else here. Hung directly overhead, the key
    // is mirrored by the whole flat interior at once and the blob comes out one even
    // tone. Brought down to roughly thirty degrees it lands instead on the annulus where
    // the surface rolls over towards the rim, and sweeps across it as a curved band that
    // follows the silhouette — which is the shape read the flat interior cannot give.
    vec3 kf = normalize(vec3(uLightDirection * 0.85, 0.52));
    vec3 kt = normalize(cross(kf, vec3(0.0, 0.0, 1.0)));
    vec3 kb = cross(kt, kf);
    float along  = dot(r, kt) / (0.95 + rough * 0.70);
    float across = dot(r, kb) / (0.58 + rough * 0.55);
    float q = sqrt(along * along + across * across);
    // Flat top, soft shoulder: a panel is uniformly bright right up to its edge.
    float key = (1.0 - smoothstep(0.45, 1.0, q)) * smoothstep(-0.05, 0.35, dot(r, kf));
    color += ENV_KEY * key;

    color += ENV_FILL * smoothstep(-0.15, 0.95, dot(r, -kf)) * 0.5;
    color += ENV_HORIZON * exp(-abs(r.z) * (9.0 - rough * 5.0));
    return color;
}

/** Colour the fluid picks up from whichever magnet is working on it. */
vec3 magnetTint(vec2 p) {
    vec3 tint = vec3(0.0);
    int count = int(uMagnetCount);
    for (int i = 0; i < MAX_MAGNETS; i++) {
        if (i >= count) break;
        vec4 magnet = uMagnets[i];
        float reach = magnetReach(magnet.z);
        float t = clamp(1.0 - length(magnet.xy - p) / reach, 0.0, 1.0);
        tint += (magnet.z >= 0.0 ? POSITIVE : NEGATIVE) * t * t;
    }
    return tint;
}

void main() {
    // Spikes need fluid to grow out of, so the crown is scaled by how much is actually
    // here — otherwise the outermost wisps sprout a full set out of nothing.
    float here = smoothstep(0.12, 0.55, density(vUV));

    vec3 field = spikeField(vWorld);
    float unstable = field.x * here;

    // Fade the lattice out as its period approaches a pixel, or it turns into shimmer.
    float pixel = fwidth(vWorld.x) + fwidth(vWorld.y);
    float resolved = smoothstep(2.0, 6.0, uSpikeParams.y / max(pixel, 1e-4));

    vec3 lattice = hexLattice(vWorld, uSpikeParams.y);
    float sharpness = uSpikeShape.x;
    // Sharpening the lattice into cones, and the matching chain rule for its gradient.
    float cone = pow(lattice.x, sharpness);
    vec2 coneGradient = sharpness * pow(lattice.x, sharpness - 1.0) * lattice.yz;

    float height = cone * unstable * resolved;
    vec2 heightGradient = coneGradient * unstable * resolved;

    // Near the rim the peaks also drag the outline out. Sampling *backwards* along the
    // outward direction pushes the iso-surface forwards, so a peak grows rather than
    // bites in.
    vec2 uv = vUV - field.yz * (height * uSpikeParams.x) / uWorldSize;
    float alpha = coverage(density(uv));

    // Soft contact shadow: sample towards the light, so this pixel is in shadow when
    // there is fluid between it and the lamp.
    float shadow = smoothstep(
        uThreshold - uEdge * 2.5,
        uThreshold + uEdge,
        density(uv + uShadowOffset)
    ) * 0.6 * (1.0 - alpha);

    if (alpha + shadow <= 0.003) discard;

    float left  = density(uv - vec2(uTexel.x, 0.0));
    float right = density(uv + vec2(uTexel.x, 0.0));
    float up    = density(uv - vec2(0.0, uTexel.y));
    float down  = density(uv + vec2(0.0, uTexel.y));

    // The body gradient is read on a deliberately wider stencil than the rim.
    //
    // They describe different things at different scales. The rim is the silhouette and
    // wants the finest measurement available; the body is the bead's doming, which is a
    // whole-blob feature. Measured one texel apart the body term instead picks up the
    // lumps left by individual particles — the field is a sum of splats, and a blur only
    // pushes that structure down, it does not remove it — and the interior comes out
    // mottled like a smudge instead of domed like liquid. Five texels is past the
    // particle spacing and still well inside the blob.
    vec2 wide = uTexel * 5.0;
    float wLeft  = density(uv - vec2(wide.x, 0.0));
    float wRight = density(uv + vec2(wide.x, 0.0));
    float wUp    = density(uv - vec2(0.0, wide.y));
    float wDown  = density(uv + vec2(0.0, wide.y));

    // Two gradients out of the same four samples.
    //
    // The raw density gradient describes the whole blob, but its transition band is as
    // wide as a splat, which shades the fluid as a soft grey mound rather than a bead of
    // something heavy. Taking the gradient of the *thresholded* surface instead gives a
    // rim whose width is set by uEdge alone, so the silhouette can be made as crisp as
    // wanted without shrinking the splats and bringing back the scalloping they smooth
    // out. The raw gradient stays, at low weight, to supply the gentle undulation across
    // the body that stops a settled puddle looking like a decal.
    vec2 rimGradient = vec2(
        coverage(right) - coverage(left),
        coverage(down) - coverage(up)
    ) * uNormalStrength;
    vec2 bodyGradient = vec2(wRight - wLeft, wDown - wUp) * uNormalStrength * uBodyRelief;

    // The lattice's gradient goes in alongside the density gradients: this is the term
    // that makes interior cones visible at all, since from overhead they are pure
    // shading and change the silhouette not at all.
    vec2 reliefGradient = heightGradient * uSpikeShape.y;

    vec3 normal = normalize(vec3(-(rimGradient + bodyGradient + reliefGradient), 0.62));

    vec3 view = vec3(0.0, 0.0, 1.0);
    vec3 light = normalize(vec3(uLightDirection, 0.72));
    vec3 halfway = normalize(light + view);

    // Roughness stands in for the surface detail this pixel cannot resolve. A settled
    // puddle is very nearly a mirror; a crown whose peaks have dropped below the pixel
    // grid has not gone away, it is only too fine to draw, so it comes back as a wider,
    // dimmer highlight instead of as aliasing.
    float rough = mix(0.06, 0.42, clamp(unstable * (1.0 - resolved), 0.0, 1.0));

    // Grazing angles reflect far more than head-on ones. On a liquid this black, that
    // Fresnel term is very nearly all you actually see.
    float fresnel = pow(1.0 - clamp(normal.z, 0.0, 1.0), 1.7);

    // Energy-normalised Blinn-Phong, tightening as the surface smooths out. The
    // normalisation is what lets the exponent move with roughness without the highlight
    // changing brightness as well as width.
    float power = mix(220.0, 12.0, rough);
    float specular = pow(max(dot(normal, halfway), 0.0), power) * (power + 2.0) * 0.02;

    vec3 environment = environmentAt(reflect(-view, normal), rough);
    // A flat average of the room, not a mirror sample. This is the distinction that was
    // missing: an ambient term is an integral over the whole hemisphere, and feeding it
    // the mirror direction instead meant a level interior — every pixel of which
    // reflects the same way — took one value for the entire blob, whatever happened to
    // lie along that single direction.
    vec3 irradiance = mix(ENV_TABLE, ENV_ROOM, 0.5) + ENV_FILL * 0.25;

    // Beer-Lambert through the depth of liquid under this pixel. Only the light that
    // goes *into* the fluid is attenuated: what the top surface mirrors does not care
    // how deep the pool is. So the split below is not cosmetic — it is what gives the
    // blob a middle instead of one flat tone.
    float thickness = max(density(uv) - uThreshold, 0.0);
    float transmission = exp(-uAbsorption * thickness);

    vec3 color = BODY;
    // Off the top surface. A dielectric reflects a few percent head-on, at any depth.
    color += irradiance * 0.05;
    // What gets into the liquid and scatters back out. This is the half depth kills.
    color += irradiance * 0.10 * transmission;
    // The mirror image of the room, concentrated where the surface rolls away.
    color += environment * (0.075 + fresnel * 1.30);
    color += SPECULAR * specular * 1.1;
    // A little of the working magnet's colour, strongest at the rim.
    color += magnetTint(vWorld) * (0.02 + fresnel * 0.10);

    // Premultiplied alpha: Pixi's normal blend expects it. The shadow contributes no
    // colour, only coverage, so it multiplies the table down rather than tinting it.
    fragColor = vec4(color * alpha, alpha + shadow * (1.0 - alpha));
}
`;

export class FerrofluidRenderer {
  /** Added to the fluid layer of the world container. */
  readonly mesh: Mesh<Geometry, Shader>;

  private readonly densityContainer = new Container({ label: 'fluid-density' });
  private readonly blurHorizontalContainer = new Container({ label: 'fluid-blur-h' });
  private readonly blurVerticalContainer = new Container({ label: 'fluid-blur-v' });

  private readonly densityTexture: RenderTexture;
  private readonly scratchTexture: RenderTexture;

  private readonly capacity: number;
  private readonly positions: Float32Array;
  private readonly positionBuffer: Buffer;
  private readonly surfaceUniforms: UniformGroup;
  private readonly densityUniforms: UniformGroup;
  private readonly blurHorizontalUniforms: UniformGroup;
  private readonly blurVerticalUniforms: UniformGroup;
  private readonly splatMesh: Mesh<Geometry, Shader>;
  private readonly textureWidth: number;
  private readonly textureHeight: number;
  /**
   * Everything measured in density units is multiplied by this.
   *
   * An 8-bit density target saturates: a settled blob sums to roughly 1.6 and the whole
   * interior clamps to 1.0, which is why the body used to carry no usable gradient. Half
   * the splat weight and half the threshold and edge with it, and the same picture lands
   * inside the representable range instead — the shader arithmetic is unchanged up to
   * this one factor, so both paths agree on what the surface looks like. On a float
   * target nothing is scaled.
   */
  private readonly densityScale: number;

  constructor(capacity: number, textureScale: number, floatDensity = false) {
    this.capacity = capacity;
    this.densityScale = floatDensity ? 1 : 0.5;

    const textureWidth = Math.max(64, Math.round(WORLD_WIDTH * textureScale));
    const textureHeight = Math.max(64, Math.round(WORLD_HEIGHT * textureScale));
    this.textureWidth = textureWidth;
    this.textureHeight = textureHeight;

    const textureOptions = {
      width: textureWidth,
      height: textureHeight,
      antialias: false,
      // One channel: density is a scalar, and the blur only ever reads .r. Half-float
      // where the driver can render into it — see the note on densityScale for what the
      // 8-bit path costs. Omitted rather than named explicitly when it cannot, so Pixi
      // picks the same default every other texture in the app gets.
      ...(floatDensity ? { format: 'r16float' as const } : {}),
      // Linear filtering is what lets five taps stand in for a nine-tap blur, and what
      // turns a low-resolution density field into a smooth surface once upscaled.
      scaleMode: 'linear',
    } as const;

    this.densityTexture = RenderTexture.create(textureOptions);
    this.scratchTexture = RenderTexture.create(textureOptions);

    // --- pass 1: splats -----------------------------------------------------
    this.positions = new Float32Array(capacity * 8);
    this.positionBuffer = new Buffer({
      data: this.positions,
      usage: BufferUsage.VERTEX | BufferUsage.COPY_DST,
    });

    const corners = new Float32Array(capacity * 8);
    const indices = new Uint32Array(capacity * 6);
    for (let i = 0; i < capacity; i++) {
      corners.set(CORNERS, i * 8);
      const v = i * 4;
      const o = i * 6;
      indices[o] = v;
      indices[o + 1] = v + 1;
      indices[o + 2] = v + 2;
      indices[o + 3] = v;
      indices[o + 4] = v + 2;
      indices[o + 5] = v + 3;
    }

    const splatGeometry = new Geometry({
      attributes: {
        aPosition: { buffer: this.positionBuffer, format: 'float32x2', stride: 8, offset: 0 },
        aCorner: {
          buffer: new Buffer({ data: corners, usage: BufferUsage.VERTEX }),
          format: 'float32x2',
          stride: 8,
          offset: 0,
        },
      },
      indexBuffer: indices,
    });

    const densityShader = Shader.from({
      gl: { vertex: densityVertex, fragment: densityFragment },
      resources: {
        densityUniforms: {
          uDensityScale: {
            value: VISUALS.densitySplatScale * this.densityScale,
            type: 'f32',
          },
        },
      },
    });
    this.densityUniforms = densityShader.resources.densityUniforms as UniformGroup;

    this.splatMesh = new Mesh({ geometry: splatGeometry, shader: densityShader, label: 'splats' });
    this.splatMesh.blendMode = 'add';
    // The density container maps world units onto the density texture's pixels.
    this.densityContainer.scale.set(textureWidth / WORLD_WIDTH, textureHeight / WORLD_HEIGHT);
    this.densityContainer.addChild(this.splatMesh);

    // --- passes 2 and 3: separable blur -------------------------------------
    const horizontal = this.createBlurPass(this.densityTexture, [1, 0]);
    this.blurHorizontalUniforms = horizontal.uniforms;
    this.blurHorizontalContainer.scale.copyFrom(this.densityContainer.scale);
    this.blurHorizontalContainer.addChild(horizontal.mesh);

    const vertical = this.createBlurPass(this.scratchTexture, [0, 1]);
    this.blurVerticalUniforms = vertical.uniforms;
    this.blurVerticalContainer.scale.copyFrom(this.densityContainer.scale);
    this.blurVerticalContainer.addChild(vertical.mesh);

    // --- pass 4: surface ----------------------------------------------------
    const surfaceShader = Shader.from({
      gl: { vertex: quadVertex, fragment: surfaceFragment },
      resources: {
        uDensity: this.densityTexture.source,
        uSampler: this.densityTexture.source.style,
        surfaceUniforms: {
          uTexel: {
            value: new Float32Array([1 / textureWidth, 1 / textureHeight]),
            type: 'vec2<f32>',
          },
          uThreshold: { value: VISUALS.densityThreshold * this.densityScale, type: 'f32' },
          uEdge: { value: VISUALS.densityEdge * this.densityScale, type: 'f32' },
          uNormalStrength: { value: VISUALS.surfaceNormalStrength, type: 'f32' },
          uBodyRelief: { value: VISUALS.bodyRelief, type: 'f32' },
          // Per density unit, so it has to be un-scaled the other way to keep the same
          // absorption over the same physical depth of fluid.
          uAbsorption: { value: VISUALS.fluidAbsorption / this.densityScale, type: 'f32' },
          uLightDirection: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
          uShadowOffset: { value: new Float32Array([0, 0]), type: 'vec2<f32>' },
          uMagnets: {
            value: new Float32Array(MAX_MAGNETS * 4),
            type: 'vec4<f32>',
            size: MAX_MAGNETS,
          },
          uMagnetCount: { value: 0, type: 'f32' },
          uFieldParams: { value: new Float32Array(3), type: 'vec3<f32>' },
          uSpikeParams: { value: new Float32Array(4), type: 'vec4<f32>' },
          uSpikeShape: { value: new Float32Array(3), type: 'vec3<f32>' },
          uWorldSize: {
            value: new Float32Array([WORLD_WIDTH, WORLD_HEIGHT]),
            type: 'vec2<f32>',
          },
        },
      },
    });
    this.surfaceUniforms = surfaceShader.resources.surfaceUniforms as UniformGroup;

    this.mesh = new Mesh({
      geometry: worldQuad(),
      shader: surfaceShader,
      label: 'fluid-surface',
    });
  }

  private createBlurPass(
    source: RenderTexture,
    axis: readonly [number, number],
  ): { mesh: Mesh<Geometry, Shader>; uniforms: UniformGroup } {
    const shader = Shader.from({
      gl: { vertex: quadVertex, fragment: blurFragment },
      resources: {
        uSource: source.source,
        uSampler: source.source.style,
        blurUniforms: {
          uDirection: { value: new Float32Array(axis), type: 'vec2<f32>' },
          uWorldSize: {
            value: new Float32Array([WORLD_WIDTH, WORLD_HEIGHT]),
            type: 'vec2<f32>',
          },
        },
      },
    });

    return {
      mesh: new Mesh({ geometry: worldQuad(), shader, label: 'fluid-blur' }),
      uniforms: shader.resources.blurUniforms as UniformGroup,
    };
  }

  /**
   * Rebuild the splat quads from the simulation.
   * `alpha` interpolates between the tick's start and end positions.
   */
  updateGeometry(fluid: Ferrofluid, alpha: number): void {
    const positions = this.positions;
    const radius = VISUALS.densitySplatRadius;
    const count = Math.min(fluid.count, this.capacity);
    const { px, py, tickStartX, tickStartY } = fluid;

    for (let i = 0; i < count; i++) {
      const x = tickStartX[i] + (px[i] - tickStartX[i]) * alpha;
      const y = tickStartY[i] + (py[i] - tickStartY[i]) * alpha;
      const o = i * 8;
      positions[o] = x - radius;
      positions[o + 1] = y - radius;
      positions[o + 2] = x + radius;
      positions[o + 3] = y - radius;
      positions[o + 4] = x + radius;
      positions[o + 5] = y + radius;
      positions[o + 6] = x - radius;
      positions[o + 7] = y + radius;
    }

    // Unused slots collapse to a degenerate quad, which rasterises nothing.
    if (count < this.capacity) positions.fill(0, count * 8);

    this.positionBuffer.update();
  }

  /** Splat, then blur. Must run before the frame that samples the result. */
  renderDensity(renderer: PixiRenderer): void {
    renderer.render({
      container: this.densityContainer,
      target: this.densityTexture,
      clear: true,
      clearColor: [0, 0, 0, 0],
    });
    renderer.render({
      container: this.blurHorizontalContainer,
      target: this.scratchTexture,
      clear: true,
      clearColor: [0, 0, 0, 0],
    });
    // Vertical pass writes back over the density texture, which the surface then samples.
    renderer.render({
      container: this.blurVerticalContainer,
      target: this.densityTexture,
      clear: true,
      clearColor: [0, 0, 0, 0],
    });
  }

  /**
   * Per-magnet ignition, with a fast attack and a slow release.
   *
   * A magnet lights up once enough fluid is inside the radius where its pull exceeds the
   * critical field, and stays lit for a while after that stops being true. That
   * asymmetry is the visible half of the Rosensweig instability's hysteresis: drag a
   * magnet into a puddle and the crown snaps up; drag it away and the crown holds for a
   * beat before collapsing.
   */
  private updateIgnition(field: MagneticField, fluid: Ferrofluid, dt: number): void {
    const magnets = field.magnets;
    if (magnets.length === 0) return;

    const required = VISUALS.spikeIgnitionParticles;
    const { px, py } = fluid;
    const count = fluid.count;

    for (const magnet of magnets) {
      // Distance at which this magnet's pull equals the critical field.
      const criticalRadius = Math.pow(
        (Math.abs(magnet.signedStrength) * PHYSICS.magneticStrength) / VISUALS.spikeCriticalForce,
        1 / PHYSICS.magneticFalloff,
      );
      const radiusSq = criticalRadius * criticalRadius;

      let inside = 0;
      for (let i = 0; i < count; i++) {
        const dx = px[i] - magnet.position.x;
        const dy = py[i] - magnet.position.y;
        if (dx * dx + dy * dy <= radiusSq && ++inside >= required) break;
      }

      const lit = inside >= required;
      const tau = lit ? 0.07 : 0.5;
      magnet.spikeIgnition += ((lit ? 1 : 0) - magnet.spikeIgnition) * (1 - Math.exp(-dt / tau));
    }
  }

  update(field: MagneticField, fluid: Ferrofluid, dt: number): void {
    // Ignition first: pack() copies it to the GPU, so it has to be current.
    this.updateIgnition(field, fluid, dt);

    const uniforms = this.surfaceUniforms.uniforms;
    uniforms.uThreshold = VISUALS.densityThreshold * this.densityScale;
    uniforms.uEdge = VISUALS.densityEdge * this.densityScale;
    uniforms.uNormalStrength = VISUALS.surfaceNormalStrength;
    uniforms.uBodyRelief = VISUALS.bodyRelief;
    uniforms.uAbsorption = VISUALS.fluidAbsorption / this.densityScale;
    this.densityUniforms.uniforms.uDensityScale = VISUALS.densitySplatScale * this.densityScale;

    const light = uniforms.uLightDirection as Float32Array;
    light[0] = VISUALS.lightDirection.x;
    light[1] = VISUALS.lightDirection.y;

    // Shadow falls away from the light, expressed in the density texture's UV space.
    const shadow = uniforms.uShadowOffset as Float32Array;
    shadow[0] = (VISUALS.lightDirection.x * VISUALS.shadowOffset) / WORLD_WIDTH;
    shadow[1] = (VISUALS.lightDirection.y * VISUALS.shadowOffset) / WORLD_HEIGHT;

    (uniforms.uMagnets as Float32Array).set(field.pack());
    uniforms.uMagnetCount = field.count;
    const params = uniforms.uFieldParams as Float32Array;
    params[0] = PHYSICS.magneticStrength;
    params[1] = PHYSICS.magneticFalloff;
    params[2] = PHYSICS.magneticMinDistance;

    const spikes = uniforms.uSpikeParams as Float32Array;
    spikes[0] = VISUALS.spikeAmplitude;
    spikes[1] = VISUALS.spikeWavelength;
    spikes[2] = VISUALS.spikeCriticalForce;
    spikes[3] = VISUALS.spikeHysteresis;

    const shape = uniforms.uSpikeShape as Float32Array;
    shape[0] = VISUALS.spikeSharpness;
    shape[1] = VISUALS.spikeRelief;
    shape[2] = 0;

    this.densityUniforms.uniforms.uDensityScale = VISUALS.densitySplatScale;

    // Blur radius is in density texels, so it is resolution independent.
    const radius = VISUALS.densityBlurRadius;
    const horizontal = this.blurHorizontalUniforms.uniforms.uDirection as Float32Array;
    horizontal[0] = radius / this.textureWidth;
    horizontal[1] = 0;
    const vertical = this.blurVerticalUniforms.uniforms.uDirection as Float32Array;
    vertical[0] = 0;
    vertical[1] = radius / this.textureHeight;
  }

  destroy(): void {
    this.mesh.destroy(true);
    this.splatMesh.destroy(true);
    this.densityTexture.destroy(true);
    this.scratchTexture.destroy(true);
  }
}

/** A quad covering the whole world box, in world coordinates. */
function worldQuad(): Geometry {
  return new Geometry({
    attributes: {
      aPosition: new Float32Array([
        0, 0, WORLD_WIDTH, 0, WORLD_WIDTH, WORLD_HEIGHT, 0, WORLD_HEIGHT,
      ]),
    },
    indexBuffer: new Uint32Array([0, 1, 2, 0, 2, 3]),
  });
}
